/**
 * Professional SVG Icon Library
 * Industry-grade icons: consistent stroke (1.5), 24×24 viewBox, round caps/joins.
 * NN/g & Baymard: clear symbols, Fitts's Law tap targets, trust signals.
 * Premium e‑commerce: emotional minimalism, recognisable at 16–24px.
 */

import React from 'react';

const DEFAULT_SIZE = 24;
const DEFAULT_STROKE = 1.5;

/**
 * Base icon wrapper — consistent styling, accessibility, optional stroke weight.
 */
const IconWrapper = ({
  children,
  size = DEFAULT_SIZE,
  strokeWidth = DEFAULT_STROKE,
  className = '',
  'aria-label': ariaLabel,
  ...props
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden={ariaLabel ? undefined : true}
    {...(ariaLabel ? { role: 'img', 'aria-label': ariaLabel } : {})}
    {...props}
  >
    {children}
  </svg>
);

// ─── Navigation & UI Icons ─────────────────────────────────────────

export const IconArrowRight = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M5 12h14M12 5l7 7-7 7" />
  </IconWrapper>
);

export const IconArrowLeft = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </IconWrapper>
);

export const IconChevronRight = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M9 18l6-6-6-6" />
  </IconWrapper>
);

export const IconChevronLeft = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M15 18l-6-6 6-6" />
  </IconWrapper>
);

export const IconChevronDown = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M6 9l6 6 6-6" />
  </IconWrapper>
);

export const IconChevronUp = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M18 15l-6-6-6 6" />
  </IconWrapper>
);

export const IconMenu = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </IconWrapper>
);

export const IconClose = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </IconWrapper>
);

export const IconSearch = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </IconWrapper>
);

/** Zoom-in (magnifier with plus) — PDP, galleries */
export const IconZoomIn = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
    <line x1="11" y1="8" x2="11" y2="14" />
    <line x1="8" y1="11" x2="14" y2="11" />
  </IconWrapper>
);

/** Info (circle with i) — toasts, modals, size-guide tip */
export const IconInfo = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </IconWrapper>
);

export const IconFilter = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    {/* Modern sliders-style filter icon (Baymard / Shopify pattern) */}
    <line x1="4" y1="6" x2="20" y2="6" />
    <circle cx="10" cy="6" r="2" />
    <line x1="4" y1="12" x2="20" y2="12" />
    <circle cx="14" cy="12" r="2" />
    <line x1="4" y1="18" x2="20" y2="18" />
    <circle cx="8" cy="18" r="2" />
  </IconWrapper>
);

// ─── Trust & Security Icons ───────────────────────────────────────

export const IconShield = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </IconWrapper>
);

export const IconLock = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </IconWrapper>
);

export const IconTruck = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <rect x="1" y="3" width="15" height="13" />
    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
    <circle cx="5.5" cy="18.5" r="2.5" />
    <circle cx="18.5" cy="18.5" r="2.5" />
  </IconWrapper>
);

export const IconReturn = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </IconWrapper>
);

// ─── Product & Shopping Icons ─────────────────────────────────────

export const IconStar = ({ size = 24, className = '', filled = false }) => (
  <IconWrapper size={size} className={className}>
    {filled ? (
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor" stroke="none" />
    ) : (
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    )}
  </IconWrapper>
);

export const IconCart = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </IconWrapper>
);

export const IconHeart = ({ size = 24, className = '', filled = false }) => (
  <IconWrapper size={size} className={className}>
    {filled ? (
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="currentColor" stroke="none" />
    ) : (
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    )}
  </IconWrapper>
);

export const IconUser = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </IconWrapper>
);

// ─── Admin Panel Icons ────────────────────────────────────────────

export const IconDashboard = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </IconWrapper>
);

export const IconPackage = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </IconWrapper>
);

export const IconShoppingBag = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </IconWrapper>
);

export const IconFolder = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </IconWrapper>
);

export const IconFolderOpen = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M5 19a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h4l2 3h9a2 2 0 0 1 2 2v1" />
    <path d="M5 19h14a2 2 0 0 0 2-2v-5a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2z" />
  </IconWrapper>
);

export const IconFileText = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </IconWrapper>
);

export const IconCookie = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <circle cx="12" cy="12" r="10" />
    <circle cx="8" cy="9" r="1.5" />
    <circle cx="15" cy="10" r="1.5" />
    <circle cx="9" cy="15" r="1.5" />
    <circle cx="16" cy="16" r="1.5" />
    <path d="M8 12h8M12 8v8" strokeWidth="1.5" />
  </IconWrapper>
);

export const IconTicket = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z" />
    <path d="M13 5v2" />
    <path d="M13 17v2" />
    <path d="M13 11v2" />
  </IconWrapper>
);

export const IconUsers = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </IconWrapper>
);

export const IconHome = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </IconWrapper>
);

// ─── Social Media Icons ───────────────────────────────────────────

export const IconInstagram = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </IconWrapper>
);

export const IconPinterest = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M12 2C6.48 2 2 6.48 2 12c0 4.84 3.44 8.87 8 9.8-.1-.9-.18-2.28.04-3.26l1.5-6.36s-.37-.74-.37-1.83c0-1.71 1-2.98 2.24-2.98 1.06 0 1.57.8 1.57 1.76 0 1.06-.68 2.64-1.03 4.1-.29 1.23.62 2.23 1.85 2.23 2.22 0 3.93-2.34 3.93-5.72 0-2.99-2.15-5.09-5.22-5.09-3.56 0-5.65 2.67-5.65 5.43 0 1.06.41 2.2.92 2.82a.3.3 0 0 1 .07.28l-.3 1.2c-.05.2-.16.25-.36.15-1.35-.63-2.19-2.6-2.19-4.19 0-3.41 2.48-6.54 7.15-6.54 3.75 0 6.67 2.67 6.67 6.24 0 3.73-2.35 6.73-5.62 6.73-1.1 0-2.14-.57-2.5-1.3l-.68 2.58c-.25.96-.93 2.16-1.38 2.9 1.04.32 2.14.49 3.28.49 5.52 0 10-4.48 10-10S17.52 2 12 2z" />
  </IconWrapper>
);

export const IconFacebook = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </IconWrapper>
);

export const IconWhatsApp = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </IconWrapper>
);

// ─── Utility Icons ───────────────────────────────────────────────

export const IconCheck = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <polyline points="20 6 9 17 4 12" />
  </IconWrapper>
);

export const IconX = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </IconWrapper>
);

export const IconPlus = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </IconWrapper>
);

export const IconMinus = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <line x1="5" y1="12" x2="19" y2="12" />
  </IconWrapper>
);

export const IconEdit = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </IconWrapper>
);

export const IconTrash = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </IconWrapper>
);

export const IconBreadcrumbSeparator = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M9 18l6-6-6-6" />
  </IconWrapper>
);

export const IconLogOut = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </IconWrapper>
);

export const IconLoader = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" strokeDasharray="32" strokeDashoffset="32">
      <animate attributeName="stroke-dasharray" dur="2s" values="0 32;16 16;0 32;0 32" repeatCount="indefinite" />
      <animate attributeName="stroke-dashoffset" dur="2s" values="0;-16;-32;-32" repeatCount="indefinite" />
    </circle>
  </IconWrapper>
);

export const IconAlertCircle = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </IconWrapper>
);

export const IconAlertTriangle = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </IconWrapper>
);

export const IconEye = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </IconWrapper>
);

export const IconTrendingUp = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </IconWrapper>
);

export const IconToggleLeft = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <rect x="1" y="5" width="22" height="14" rx="7" ry="7" />
    <circle cx="8" cy="12" r="3" fill="currentColor" />
  </IconWrapper>
);

export const IconToggleRight = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <rect x="1" y="5" width="22" height="14" rx="7" ry="7" />
    <circle cx="16" cy="12" r="3" fill="currentColor" />
  </IconWrapper>
);

export const IconImage = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </IconWrapper>
);

export const IconExternalLink = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </IconWrapper>
);

export const IconDollarSign = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </IconWrapper>
);

export const IconClock = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </IconWrapper>
);

export const IconMapPin = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </IconWrapper>
);

export const IconRuler = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <line x1="2" y1="12" x2="22" y2="12" />
    <line x1="4" y1="10" x2="4" y2="14" />
    <line x1="8" y1="10" x2="8" y2="14" />
    <line x1="12" y1="10" x2="12" y2="14" />
    <line x1="16" y1="10" x2="16" y2="14" />
    <line x1="20" y1="10" x2="20" y2="14" />
  </IconWrapper>
);

export const IconCreditCard = ({ size = 24, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <rect x="2" y="4" width="20" height="16" rx="2" ry="2" />
    <line x1="2" y1="9" x2="22" y2="9" />
    <line x1="7" y1="16" x2="11" y2="16" />
  </IconWrapper>
);